import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button, EmptyState, Spinner, Switch } from "@cntrl/ui";
import { useAccount } from "../../app/hooks/useAccount.js";
import { useBandContextOrNull } from "../../app/bandContext.js";
import { useThemePreference, THEME_TOGGLE_AVAILABLE } from "../../app/theme.js";
import { useTouchPickerPreview, TOUCH_PICKER_PREVIEW_AVAILABLE } from "../../app/touchPickerPreview.js";
import { usePlan } from "../../app/hooks/useBilling.js";
import { billingLine, planDate, planName } from "../billing/planFormat.js";
import { PlanDialog } from "../billing/PlanDialog.js";
import { canPurchaseHere } from "../../app/inAppPurchase.js";
import { RoleLabel } from "../contacts/RoleLabel.js";
import { ChangePasswordButton } from "./ChangePasswordButton.js";
import { CommunicationPreferencesButton } from "./communicationPreferences/CommunicationPreferencesButton.js";
import { GoogleCalendarSection } from "./GoogleCalendarSection.js";
import { MfaSection } from "./MfaSection.js";
import { AppVersion } from "./AppVersion.js";
import { FixtureSwitchVisibilityToggle } from "../dev/FixtureSwitchVisibilityToggle.js";
import { CalendarSyncSection } from "./CalendarSyncSection.js";
import styles from "./SettingsScreen.module.scss";

/** Format an ISO member-since date to the viewer's locale (12-hour/local-tz house rule — date-only here). */
function formatMemberSince(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

/**
 * Account tab — a read-only account table (artist / role / email / member-since) plus the
 * change-password form. Change-password calls the EXISTING `POST /auth/change-password`; the returned fresh
 * token replaces the caller's session via the TokenProvider (in useChangePassword). A wrong current password
 * surfaces the server 4xx verbatim inline — never a phantom success.
 */
export function SettingsAccountTab() {
  const account = useAccount();
  const band = useBandContextOrNull();
  // The dialog's open state lives HERE rather than inside PlanRows, so the dialog itself renders outside the
  // <dl>. `Modal` does not portal, and a dialog nested in a definition list is markup nobody should have to
  // reason about — the rows stay rows.
  const [planDialogOpen, setPlanDialogOpen] = useState(false);

  return (
    <div className={styles.stack}>
      {/* One surface: section labels sit on the modal canvas and only the account ROWS get a paper card
          (the spec's `.account-list`). Wrapping each group in its own white card is the shape the spec
          calls out as wrong. */}
      <section>
        {account.isLoading ? (
          <div className={styles.center} role="status" aria-live="polite">
            <Spinner size={20} label="Loading account…" />
          </div>
        ) : account.isError ? (
          <EmptyState
            title="Couldn't load your account"
            description={account.error instanceof Error ? account.error.message : "Please try again."}
            action={<Button onClick={() => void account.refetch()}>Retry</Button>}
          />
        ) : (
          <dl className={styles.accountList}>
            <div className={styles.accountRow}>
              <dt className={styles.accountKey}>Your artist</dt>
              <dd className={styles.accountVal}>{account.data?.artist || "—"}</dd>
            </div>
            <div className={styles.accountRow}>
              <dt className={styles.accountKey}>Your role</dt>
              <dd className={styles.accountVal}>
                {account.data?.role ? <RoleLabel role={account.data.role} owner={account.data.isOwner} /> : "—"}
              </dd>
            </div>
            <div className={styles.accountRow}>
              <dt className={styles.accountKey}>Email</dt>
              <dd className={styles.accountVal}>{account.data?.email || "—"}</dd>
            </div>
            <div className={styles.accountRow}>
              <dt className={styles.accountKey}>Member since</dt>
              <dd className={styles.accountVal}>{formatMemberSince(account.data?.memberSince)}</dd>
            </div>
            <PlanRows onChangePlan={() => setPlanDialogOpen(true)} />
          </dl>
        )}
        {/* Under the account details, not in a Password section of its own (PO, 2026-09-06): changing a
            password is a rare errand off the account you are reading, not a second subject on the tab.
            The ONE change-password implementation — the org Settings modal's Account tab opens the same
            dialog. It is person-scoped (no tenant in the route), which is what lets both shells share it. */}
        <div className={styles.accountLinks}>
          <CommunicationPreferencesButton />
          <ChangePasswordButton />
        </div>
      </section>

      {/* The band is NAMED in the dialog (PO, 2026-09-10) — `account.artist` is this band's own name on
          this seat, which is the same fact the "Your artist" row above prints. */}
      {planDialogOpen && (
        <PlanDialog
          bandId={band?.bandId}
          bandName={account.data?.artist}
          onClose={() => setPlanDialogOpen(false)}
        />
      )}

      {/* ART-317: the person's own sign-in codes. */}
      <MfaSection />

      <GoogleCalendarSection />

      <CalendarSyncSection />

      <AppearanceSection />

      <FixtureSwitchVisibilityToggle />

      <AppVersion />
    </div>
  );
}

/**
 * DEV-ONLY appearance control (ART-174). The dark palette is wired across the whole app, but the toggle that
 * turns it on lives only in non-production lanes (`THEME_TOGGLE_AVAILABLE`) until each page is polished — at
 * which point that guard is deleted and this section ships to everyone from exactly here. It is the app-wide
 * preference for ordinary surfaces; the Day-of-Show screen is always dark on mobile independent of it.
 *
 * The preference is per-device (a cookie — localStorage is lint-banned) for the trial; see `app/theme.ts`.
 * Promotion moves it to an account-backed setting.
 */
function AppearanceSection() {
  const preference = useThemePreference((s) => s.preference);
  const setPreference = useThemePreference((s) => s.setPreference);
  const touchPickers = useTouchPickerPreview((s) => s.enabled);
  const setTouchPickers = useTouchPickerPreview((s) => s.setEnabled);

  if (!THEME_TOGGLE_AVAILABLE) return null;

  return (
    <>
      <div className={styles.toggleRow}>
        <span className={styles.toggleText}>
          <span className={styles.toggleLabel}>Dark theme</span>
          <span className={styles.toggleDesc}>(DEBUG) Preview the dark theme on this device.</span>
        </span>
        <Switch
          checked={preference === "dark"}
          onChange={(next) => setPreference(next ? "dark" : "light")}
          label="Dark theme"
        />
      </div>
      {TOUCH_PICKER_PREVIEW_AVAILABLE && (
        <div className={styles.toggleRow}>
          <span className={styles.toggleText}>
            <span className={styles.toggleLabel}>Touch dropdowns</span>
            <span className={styles.toggleDesc}>
              (DEBUG) Use phone-style dropdowns when the window is phone-width.
            </span>
          </span>
          <Switch checked={touchPickers} onChange={setTouchPickers} label="Touch dropdowns" />
        </div>
      )}
    </>
  );
}

/**
 * **Plan + Billing — two rows in the account list, and they render in EVERY scenario** (PO, 2026-09-10).
 *
 * PLAN says WHAT the band is on and WHERE it is billed from; BILLING says WHEN. A Shopify-billed band, a
 * comped band and a lifetime band all see them — what varies is only the row's right edge. The earlier shape
 * hid the whole surface from those three, which told a Shopify merchant nothing at all about a subscription
 * they pay for every month.
 *
 * **The split is by QUESTION, not by convenience** (PO, 2026-09-11): PLAN names the product, BILLING carries
 * the cadence, the source and the date. "Artist CNTRL Pro · Annual · Stripe" on one row left the BILLING row
 * holding a bare date while three facts about the money sat on the row about the product.
 *
 * **The action has THREE outcomes, and the server decides which** (`plan.action`):
 *
 * - `change` — the Stripe rail and this caller may act: **Change plan**, opening {@link PlanDialog}. A red
 *   LINK, and it leads the row rather than trailing it (PO, 2026-09-11): a pill on a row of plain values read
 *   as the loudest thing on the tab, which is the wrong weight for an errand most bands run once a year.
 * - `shopify` — a sentence saying where the billing lives, with **no link**. Sending a merchant out of the app
 *   toward a billing screen is the steering the Shopify review objects to; the sentence alone is the safer
 *   half (PO). It stays on the value's right: it is a fact, not an action, and it does not take the slot.
 * - `none` — a comp, a lifetime, or a caller who may not change it. Nothing, because here the missing button
 *   is either about a plan nobody can buy or about the reader, and neither is worth announcing on the row.
 *
 * Collapsing `shopify` into `none` is what left a merchant staring at a row with no account of why it has no
 * button — the hold countdown's running / elapsed / no-deadline split, one surface over.
 *
 * The rows are NOT gated on who is reading. What the band pays for is a fact about the account, like the
 * email beside it; only CHANGING it is the money team's, and that is what `action` carries.
 */
function PlanRows({ onChangePlan }: { onChangePlan: () => void }) {
  const navigate = useNavigate();
  const onChoosePlan = () => void navigate({ to: "/activate" });
  const band = useBandContextOrNull();
  const plan = usePlan(band?.bandId);

  if (!plan.data) return null;

  const { billedVia, planKey, options, currentPlan, pendingPlanKey, pendingPlanOn, pendingCancel, currentPeriodEnd } =
    plan.data;
  // Inside the app there is no way to pay us (ART-346): the row names the plan and offers no Change / Choose.
  const action = canPurchaseHere() ? plan.data.action : null;
  const renews = planDate(currentPeriodEnd);
  // `options` is what a band may BUY; the plan it is ON may not be in it (a custom rate seeded inactive). The
  // server serves that row as `currentPlan`, so the name and cadence come from the catalogue either way.
  const named = currentPlan ? [...(options ?? []), currentPlan] : options;

  // THREE outcomes, and the first two must not collapse: a subscription that ENDS is not one that CHANGES,
  // and neither is a renewal. A band that has asked to leave and reads "Renews 12 Oct" has been told the
  // opposite of what it asked for. Null rather than an em dash — `billingLine` owns the empty case, because
  // it is the one that knows whether the cadence and the source had anything to say either.
  const timing = pendingCancel && pendingPlanOn ? `Ends ${planDate(pendingPlanOn)}`
    : pendingPlanKey && pendingPlanOn ? `Changes ${planDate(pendingPlanOn)}`
      // A comp's period end is when the grant RUNS OUT (BillingStatus.CompExpired) — nothing renews it. A free
      // trial's is when the trial ends (ART-348).
      : renews && (billedVia === "comp" || billedVia === "trial") ? `Ends ${renews}`
      : renews ? `Renews ${renews}`
        : null;

  return (
    <>
      <div className={styles.accountRow}>
        <dt className={styles.accountKey}>Plan</dt>
        <dd className={styles.accountVal}>
          {/* The action LEADS the row. `variant="link"` is the app's own link treatment (accent red, no pill,
              underline on hover) — reached for on the primitive rather than restyled locally. Danger red is
              deliberately not used: changing a plan is not destructive, and that ink belongs to the one
              control in this feature that is (Cancel subscription). */}
          {action === "change" && (
            <Button variant="link" onClick={onChangePlan}>
              Change plan
            </Button>
          )}
          {/* On the free trial there is nothing to change yet: the band picks the plan it will pay for, on the
              same screen the paywall uses (ART-348). */}
          {action === "choose" && (
            <Button variant="link" onClick={onChoosePlan}>
              Choose plan
            </Button>
          )}
          <span className={styles.planValue}>{planName(planKey, named)}</span>
          {action === "shopify" && (
            <span className={styles.planNote}>Manage billing in your Shopify admin</span>
          )}
        </dd>
      </div>
      <div className={styles.accountRow}>
        <dt className={styles.accountKey}>Billing</dt>
        <dd className={styles.accountVal}>{billingLine(planKey, billedVia, named, timing)}</dd>
      </div>
    </>
  );
}

